
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './index.module.css';
import { Button, DatePicker, Modal } from '../..';
import { RideDestinationInfo } from "../../common/RideDetails/RideDetailsPartials/RideDestinationInfo";
import { RidePickUpInfo } from "../../common/RideDetails/RideDetailsPartials/RidePickUpInfo";
import classNames from 'classnames';
import { CONFIRM_ROUNDTRIP_RIDE_CONTENT, CONFIRM_ROUNDTRIP_RIDE_TITLE, DEFAULT_TIME_FORMAT } from '../../../constants/constants';
import { FormikProvider, useFormik } from 'formik';
import { useDateTimeHandlers } from '../../forms/CreateRide/StepThree/useDateTimeHandlers';
import { DateHelper } from '../../../utils';

export const useConfirmRoundTripRideModal = ({ ride, onConfirm, setFormikValues,
    minDelayToStartRide = { hours: 0, minutes: 15 },
    minimumRoundTrip = { hours: 0, minutes: 15 },
    maxPickUpWindowOfTime
}) => {
    const [visibleModal, setVisibleModal] = useState(false);
    const [pickUpDateLowerLimitRange, setPickUpDateLowerLimitRange] = useState();
    const [pickUpDateHigherLimitRange, setPickUpDateHigherLimitRange] = useState();
    const [returnDateLowerLimitRange, setReturnDateLowerLimitRange] = useState();
    const [returnDateHigherLimitRange, setReturnDateHigherLimitRange] = useState();
    const [ridePickUpDate, setRidePickUpDate] = useState();
    const [returnPickUpDate, setReturnPickUpDate] = useState();

    useEffect(() => {
        if (ride) {
            setPickUpDateHigherLimitRange(DateHelper.utcToZonedTime(ride?.pickUpDate?.datetime, ride?.pickUpDate?.timezone));
            setPickUpDateLowerLimitRange(
                DateHelper.utcToZonedTime(ride?.pickUpDateLowerLimit?.datetime, ride?.pickUpDateLowerLimit?.timezone),
            );
        }

        if (ride?.isRoundtrip && ride?.connectedLeg) {
            const returnPickUpDate = ride?.connectedLeg.pickUpDate;
            const returnLowerDate = ride?.connectedLeg.pickUpDateLowerLimit;

            setReturnDateHigherLimitRange(DateHelper.utcToZonedTime(returnPickUpDate?.datetime, returnPickUpDate?.timezone));
            setReturnDateLowerLimitRange(
                DateHelper.utcToZonedTime(returnLowerDate?.datetime, returnLowerDate?.timezone),
            );
        }

        return;
    }, [ride]);

    const InitialValues = useMemo(() => {
        let pickUpDate = ride?.pickUpDate;
        let returnTime;
        let isRoundtrip = false;
        if (ride?.isRoundtrip && ride?.connectedLeg) {
            isRoundtrip = true;

            if (ride?.leg.toLowerCase() === "b") {
                pickUpDate = ride?.connectedLeg?.pickUpDate;
                returnTime = ride?.pickUpDate;

            } else {
                returnTime = ride?.connectedLeg?.pickUpDate;
            }
        }

        return {
            pickUpDate: pickUpDate || null,
            returnTime: returnTime || null,
            isRoundtrip: isRoundtrip,
        };
    }, [ride?.pickUpDate, ride?.returnTime]);

    const {
        disabledDatesForReturnTime,
        pickUpDate,
        returnTimeDate,
        returnTimeLowerLimit,
    } = useDateTimeHandlers({
        defaultValues: InitialValues,
        minDelayToStartRide,
        maxPickUpWindowOfTime,
        minimumRoundTrip,
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: InitialValues,
        onSubmit: onConfirm,
    });

    const minAvailablePickUpDate = useMemo(() =>
        DateHelper.addDates(DateHelper.utcToZonedTime(Date.now(), ride?.pickUpDate?.timezone), minDelayToStartRide),
        [ride?.pickUpDate?.timezone, minDelayToStartRide]);


    const onCancel = useCallback(async () => {
        await setVisibleModal(false);
    }, [setVisibleModal])


    const minAvailableReturnDate = useMemo(() =>
        DateHelper.addDates(pickUpDate, minimumRoundTrip),
        [pickUpDate, minimumRoundTrip]);

    const pickUpDefaultDateTime = useMemo(() => new Date().setHours(0, 0, 0, 0), []);

    const returnTimeDefaultDateTime = useMemo(() => new Date().setHours(0, 0, 0, 0), []);

    const disabledDatesForPickUpDate = (d) => {
        const minDate = pickUpDateLowerLimitRange;
        const maxDate = pickUpDateHigherLimitRange;

        return (
            (minDate != null && DateHelper.isBefore(d, minDate) && !DateHelper.isTheSameDay(d, minDate)) ||
            (maxDate != null && DateHelper.isAfter(d, maxDate) && !DateHelper.isTheSameDay(d, maxDate))
        );
    };

    const disabledDateTime = (currentDate) => {
        const startDate = pickUpDateLowerLimitRange;
        const maxDate = pickUpDateHigherLimitRange;

        return DateHelper.disabledTimeForHigherLimit({ currentDate, startDate, maxDate });
    };

    const diabledDatesForReturnField = (d) => {
        const minDate = returnDateLowerLimitRange;
        const maxDate = returnDateHigherLimitRange;

        return (
            (minDate != null && DateHelper.isBefore(d, minDate) && !DateHelper.isTheSameDay(d, minDate)) ||
            (maxDate != null && DateHelper.isAfter(d, maxDate) && !DateHelper.isTheSameDay(d, maxDate))
        );
    };

    const disabledDateTImeForReturnField = (currentDate) => {
        const startDate = returnDateLowerLimitRange;
        const maxDate = returnDateHigherLimitRange;

        return DateHelper.disabledTimeForHigherLimit({ currentDate, startDate, maxDate });
    };

    const handlePickUpDateTime = ((obj) => {
        setRidePickUpDate(obj.values.pickUpDate);
    });

    const handleReturnTime = (obj) => {
        setReturnPickUpDate(obj.values.returnTime);
    }

    const handleRideAccept = () => {
        onConfirm({ ridePickUpDate, returnPickUpDate });
    }

    return {
        Modal: (

            <Modal
                visible={visibleModal}
                onCancel={onCancel}
                closable
                width={800}
                bodyStyle={{ height: '40%', padding: '20px 20px 0' }}
                title={CONFIRM_ROUNDTRIP_RIDE_TITLE}>
                <div className={styles.modalContent}>
                    <h3 className={styles.roundTripHeading}>
                        {CONFIRM_ROUNDTRIP_RIDE_CONTENT}
                    </h3>
                    <div className={styles.routes}>
                        <RidePickUpInfo
                            pickupAddressDetails={ride?.pickupAddressDetails}
                            destinationAddress={ride?.destinationAddress}
                            pickUpDate={ride?.pickUpDate}
                            riderDescription={ride?.riderDescription}
                            pickUpDateLowerLimit={ride?.pickUpDateLowerLimit}
                            isPickUpDateFlexible={ride?.isPickUpDateFlexible}
                            tcEstimatedPickUpDate={ride?.tcEstimatedPickUpDate}
                            securityGate={ride?.pickUpSecurityGateDetails}
                            flightInfo={ride?.flightInfo}
                            appointmentDate={ride?.appointmentDate}
                            isRecurringRide={ride?.isRecurring}
                            leg={ride?.leg}
                            seqId={ride?.seqId}
                        />

                        <div className={styles.destionationRouteInfo}>
                            <RideDestinationInfo
                                destinationAddress={ride?.destinationAddress}
                                originAddress={ride?.pickupAddressDetails}
                                showRoundtripInfo={
                                    ride?.isRoundtrip && ride?.leg
                                }
                                securityGate={ride?.destinationSecurityGateDetail}
                                returnTime={ride?.connectedLeg?.pickUpDate}
                                returnTimeLowerLimit={ride?.returnTimeLowerLimit}
                                isReturnDateFlexible={ride?.isReturnDateFlexible}
                                leg={ride?.connectedLeg?.leg}
                                seqId={ride?.connectedLeg?.seqId}
                            />
                        </div>


                    </div>

                    <FormikProvider value={formik}>
                        <form onSubmit={formik?.handleSubmit} className={styles.form}>
                            <div className={classNames({
                                [styles.modalBtns]: true,
                                [styles.cancelBtns]: true,
                            })} style={{ marginBottom: '10px' }}>
                                {ride?.isPickUpDateFlexible &&
                                    <label style={{ marginRight: '10px' }} >
                                        <span className={styles.rescheduleInputLabel}>Leg A Pick Up Date & Time</span>
                                        <DatePicker
                                            name="pickUpDate.datetime"
                                            allowClear
                                            onChange={handlePickUpDateTime}
                                            format={DEFAULT_TIME_FORMAT}
                                            value={pickUpDate}
                                            showTime={{ defaultValue: pickUpDefaultDateTime }}
                                            showNow={false}
                                            use12Hours
                                            disabledDate={disabledDatesForPickUpDate}
                                            disabledTime={disabledDateTime}
                                            inputReadOnly
                                            getPopupContainer={(trigger) => trigger}
                                        />
                                    </label>
                                }
                                {ride?.connectedLeg?.isPickUpDateFlexible &&
                                    <label  >
                                        <span className={styles.rescheduleInputLabel}>Leg B Pickup Date & Time</span>
                                        <DatePicker
                                            name='returnTime.datetime'
                                            myClassName={styles.datepicker}
                                            onChange={handleReturnTime}
                                            format={DEFAULT_TIME_FORMAT}
                                            value={returnTimeDate}
                                            showTime={{ defaultValue: returnTimeDefaultDateTime }}
                                            showNow={false}
                                            use12Hours
                                            disabledDate={diabledDatesForReturnField}
                                            disabledTime={disabledDateTImeForReturnField}

                                            inputReadOnly
                                            allowClear
                                            getPopupContainer={(trigger) => trigger}
                                        />
                                    </label>
                                }
                            </div>
                        </form>


                    </FormikProvider>


                    <div className={classNames({
                        [styles.modalBtns]: true,
                        [styles.cancelBtns]: true,
                    })}>
                        <Button myClassName={styles.navigationBtn} onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button myClassName={styles.navigationBtn} onClick={handleRideAccept} >
                            Accept Roundtrip Ride
                        </Button>



                    </div>
                </div>
            </Modal>



        ),
        setConfirmRoundTripRideModalVisible: setVisibleModal,
        ConfirmRoundTripRideModalVisible: visibleModal

    };
};